import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { showToast } from './Toast';
import VideoWatermarkProgressModal from './VideoWatermarkProgressModal';
import PaywallModal from './PaywallModal';
import { useAnalytics } from '../hooks/useAnalytics';

const ShareVideoModal = ({
  isOpen, onClose, videoUrl, storyTitle, videoRef,
  user = null, subscriptionStatus = null, apiClient = null,
  storyId = null, onShare = null, onDownload = null,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isUnsupported, setIsUnsupported] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [isDownloadProgressModalOpen, setIsDownloadProgressModalOpen] = useState(false);
  const { trackContentShare } = useAnalytics();
  const [isPaywallModalOpen, setIsPaywallModalOpen] = useState(false);

  const isPremium = ['STRIPE','IAP','EDDY','PREMIUM','NIKHIL','PHILIP','BYPASS','STARTER','PLUS','FAMILY','UNLIMITED'].includes(subscriptionStatus?.subscriptionType);

  useEffect(() => {
    if (isOpen) { setIsProcessing(false); setIsReady(true); setIsUnsupported(false); setVideoFile(null); }
  }, [isOpen, videoUrl, storyTitle]);

  const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  const handleShare = async (platform, event) => {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (!videoUrl) return;
    trackContentShare('video', storyId, platform.toLowerCase());
    if (onShare) onShare();

    if (videoRef?.current) {
      try { localStorage.setItem('pixley_video_state', JSON.stringify({ currentTime: videoRef.current.currentTime || 0, isPaused: videoRef.current.paused, timestamp: Date.now() })); } catch (e) {}
    }

    const shareUrl = window.location.href;
    const encodedUrl = encodeURIComponent(shareUrl);
    const shareTextPlain = `Check out this personalized story I made using Pixley!\n\n${shareUrl}`;
    const shareTextEncoded = encodeURIComponent(shareTextPlain);
    const mobile = isMobile();
    let targetUrl = '', fallbackUrl = '';

    if (platform === 'Instagram') {
      try { await navigator.clipboard.writeText(shareTextPlain); showToast('Text copied! Redirecting...', 'success'); await new Promise(r => setTimeout(r, 2000)); } catch (e) { showToast('Could not copy caption.', 'error'); return; }
      onClose();
      if (mobile) { targetUrl = 'instagram://'; fallbackUrl = 'https://www.instagram.com/'; }
      else { window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer'); return; }
    } else if (platform === 'Facebook') {
      try { await navigator.clipboard.writeText(shareTextPlain); showToast('Text copied! Redirecting...', 'success'); await new Promise(r => setTimeout(r, 2000)); } catch (e) { showToast('Could not copy text.', 'error'); return; }
      onClose();
      fallbackUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      if (mobile) { targetUrl = `fb://share?href=${encodedUrl}`; } else { window.open(fallbackUrl, '_blank', 'noopener,noreferrer'); return; }
    } else if (platform === 'X') {
      onClose();
      fallbackUrl = `https://twitter.com/intent/tweet?text=${shareTextEncoded}`;
      if (mobile) { targetUrl = `twitter://post?message=${shareTextEncoded}`; } else { window.open(fallbackUrl, '_blank', 'noopener,noreferrer'); return; }
    } else { return; }

    if (mobile && targetUrl) {
      const link = document.createElement('a'); link.href = targetUrl; link.style.display = 'none';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => { if (fallbackUrl) window.open(fallbackUrl, '_blank', 'noopener,noreferrer'); }, 800);
    }
  };

  if (!isOpen) return null;

  const btnBase = { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "14px 20px", color: "#FFF", fontWeight: 700, fontSize: 15, borderRadius: 18, border: "none", cursor: "pointer", fontFamily: "'Fredoka', sans-serif", transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)" };
  const press = (shadow) => ({
    onMouseDown: (e) => { e.currentTarget.style.transform = "translateY(4px)"; e.currentTarget.style.boxShadow = "0px 0px 0px " + shadow; },
    onMouseUp: (e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0px 4px 0px " + shadow; },
    onMouseLeave: (e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0px 4px 0px " + shadow; },
  });

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[70] p-4" style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#FFF8E7", borderRadius: 28, boxShadow: "0px 5px 0px rgba(11,26,51,0.12), 0px 24px 64px rgba(11,26,51,0.24)", maxWidth: 420, width: "100%", fontFamily: "'Inter', 'Fredoka', sans-serif" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 0" }}>
          <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 24, fontWeight: 700, color: "#0065F8", margin: 0 }}>Share Your Story</h2>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: 12, background: "#FFFDF7", border: "none", cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#334155" }}>✕</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {isProcessing && (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ width: 48, height: 48, border: "4px solid rgba(0,101,248,0.15)", borderTopColor: "#0065F8", borderRadius: "50%", margin: "0 auto 16px" }} className="animate-spin" />
              <p style={{ color: "#0B1A33", fontSize: 16, fontWeight: 600, fontFamily: "'Fredoka', sans-serif" }}>Preparing your video...</p>
            </div>
          )}

          {isReady && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button type="button" onClick={(e) => handleShare('Instagram', e)} style={{ ...btnBase, background: "linear-gradient(135deg, #833AB4, #E1306C, #F77737)", boxShadow: "0px 4px 0px #6B2D9E" }} {...press("#6B2D9E")}>
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.98-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.98-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                Instagram
              </button>
              <button type="button" onClick={(e) => handleShare('Facebook', e)} style={{ ...btnBase, background: "linear-gradient(180deg, #1A94FF 0%, #0065F8 100%)", boxShadow: "0px 4px 0px #073B8E" }} {...press("#073B8E")}>
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>
              <button type="button" onClick={(e) => handleShare('X', e)} style={{ ...btnBase, background: "#0B1A33", boxShadow: "0px 4px 0px #000" }} {...press("#000")}>
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X
              </button>
            </div>
          )}
        </div>

        <div style={{ padding: "0 24px 20px" }}>
          <button onClick={onClose} style={{ width: "100%", padding: "12px 20px", background: "#FFFDF7", color: "#334155", border: "2px solid rgba(11,26,51,0.08)", borderRadius: 18, fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.08)", fontFamily: "'Fredoka', sans-serif" }}>
            Close
          </button>
        </div>
      </div>

      <VideoWatermarkProgressModal isOpen={isDownloadProgressModalOpen} onClose={() => { setIsDownloadProgressModalOpen(false); onClose(); }} storyId={storyId} apiClient={apiClient} storyTitle={storyTitle} user={user} />
      <PaywallModal isOpen={isPaywallModalOpen} onClose={() => { setIsPaywallModalOpen(false); onClose(); }} starCost={0} currentStars={0} contentType="download" apiClient={apiClient} user={user} />
    </div>
  );
};

export default ShareVideoModal;
